import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';

// Core funnel routes stay in the main bundle — these are the pages a new
// visitor hits in their first session and must paint instantly.
import Home from '@/pages/Home';
import RegistryOnboarding from '@/pages/RegistryOnboarding';
import RegistryViewEnhanced from '@/pages/RegistryViewEnhanced';
import RegistryManage from '@/pages/RegistryManage';
import Dashboard from '@/pages/Dashboard';
import HowItWorks from '@/pages/HowItWorks';
import ExploreGifts from '@/pages/ExploreGifts';
import AskJB from '@/pages/AskJB';
import RegistrySearch from '@/pages/RegistrySearch';
import NotFound from '@/pages/NotFound';

import { AuthProvider } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import CookieConsent from '@/components/CookieConsent';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Secondary routes are code-split — admin tooling, the long-form guide, and
// legal pages shouldn't cost every first-time visitor download time.
const AdminProducts = lazy(() => import('@/pages/AdminProducts'));
const BabyRegistryGuide = lazy(() => import('@/pages/BabyRegistryGuide'));
const BrandProducts = lazy(() => import('@/pages/BrandProducts'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const Contact = lazy(() => import('@/pages/Contact'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const RefundPolicy = lazy(() => import('@/pages/RefundPolicy'));

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Minimal route-transition fallback — brand-toned, no layout shift drama.
const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <span className="inline-block h-6 w-6 rounded-full border-2 border-clay-300 border-t-clay-600 animate-spin" aria-label="Loading" />
  </div>
);

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/registry/new" element={<RegistryOnboarding />} />
            <Route path="/registry/:id" element={<RegistryViewEnhanced />} />
            <Route path="/registry/:id/edit" element={<RegistryManage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/explore-gifts" element={<ExploreGifts />} />
            {/* Ask JB — replaces the old Guides system */}
            <Route path="/ask-jb" element={<AskJB />} />
            <Route path="/guides" element={<Navigate to="/ask-jb" replace />} />
            <Route path="/guides/:topic" element={<Navigate to="/ask-jb" replace />} />
            {/* Long-form flagship guide stays — separate from Ask JB */}
            <Route path="/baby-registry-guide" element={<BabyRegistryGuide />} />
            <Route path="/brand/:brandName" element={<BrandProducts />} />
            <Route path="/search-registry" element={<RegistrySearch />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />

            {/* Legacy redirect — Joy Basket isn't a checkout product */}
            <Route path="/cart" element={<Navigate to="/explore-gifts" replace />} />

            {/* Real 404 — no more soft-404 redirect to home */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <CookieConsent />
      </div>
    </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
